import { useEffect, useRef, useState } from 'react';
import { getInputAdapter } from '@/services/input';

const SIZE = 100;
const HEAD = 36;
const DEAD_ZONE = 0.15;

export function VirtualJoystick() {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ dx: number; dy: number } | null>(null);
  const [touchActive, setTouchActive] = useState(false);

  // Esconde em dispositivos sem touch
  const isTouchDevice =
    typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

  useEffect(() => {
    return () => {
      getInputAdapter().setJoystick(false, 0, 0);
    };
  }, []);

  if (!isTouchDevice) return null;

  const handleStart = (clientX: number, clientY: number): void => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    updateFrom(cx, cy, clientX, clientY);
    setTouchActive(true);
  };

  const handleMove = (clientX: number, clientY: number): void => {
    if (!touchActive || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    updateFrom(cx, cy, clientX, clientY);
  };

  const handleEnd = (): void => {
    setTouchActive(false);
    setPos(null);
    getInputAdapter().setJoystick(false, 0, 0);
  };

  const updateFrom = (cx: number, cy: number, clientX: number, clientY: number): void => {
    let dx = (clientX - cx) / (SIZE / 2);
    let dy = (clientY - cy) / (SIZE / 2);
    const mag = Math.hypot(dx, dy);
    if (mag > 1) {
      dx /= mag;
      dy /= mag;
    }
    if (mag < DEAD_ZONE) {
      dx = 0;
      dy = 0;
    }
    setPos({ dx, dy });
    getInputAdapter().setJoystick(true, dx, dy);
  };

  return (
    <div
      ref={ref}
      data-testid="virtual-joystick"
      onTouchStart={(e) => {
        e.preventDefault();
        const t = e.touches[0];
        if (t) handleStart(t.clientX, t.clientY);
      }}
      onTouchMove={(e) => {
        e.preventDefault();
        const t = e.touches[0];
        if (t) handleMove(t.clientX, t.clientY);
      }}
      onTouchEnd={(e) => {
        e.preventDefault();
        handleEnd();
      }}
      style={{
        position: 'fixed',
        left: 24,
        bottom: 24,
        width: SIZE,
        height: SIZE,
        borderRadius: '50%',
        background: 'rgba(20,20,30,0.6)',
        border: '2px solid rgba(255,255,255,0.2)',
        zIndex: 95,
        touchAction: 'none',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: SIZE / 2 - HEAD / 2 + (pos?.dx ?? 0) * (SIZE / 2 - HEAD / 2),
          top: SIZE / 2 - HEAD / 2 + (pos?.dy ?? 0) * (SIZE / 2 - HEAD / 2),
          width: HEAD,
          height: HEAD,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.6)',
          border: '2px solid rgba(255,255,255,0.8)',
          transition: touchActive ? 'none' : 'all 0.15s',
        }}
      />
    </div>
  );
}
